/*
 *
 * CabinetRibbon
 *
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import reducer from './reducer';
import { RootState } from './types';
import RibbonSection from 'containers/Ribbon/RibbonSection';
import DimensionCabinetSelector from './DimensionCabinetSelector';
import {
  addShelf,
  setDepthRequest,
  setWidthRequest,
} from 'containers/Configurator/actions/furniture';
import sizeDepth from 'assets/images/size/depth.svg';
import sizeWidth from 'assets/images/size/width.svg';
import noFeets from 'assets/images/support/no_feets.png';
import feets10 from 'assets/images/support/feets_10.png';
import feetsAdjustable10 from 'assets/images/support/feets_adjustable_10.png';
import wheelsBlack from 'assets/images/support/wheelsBlack.png';
import wheelsWhite from 'assets/images/support/wheelsWhite.png';
import addUS from 'assets/images/ribbons/cabinetRibbon/upperShelf/AddUpperShelf.png';
import whiteUS from 'assets/images/ribbons/cabinetRibbon/upperShelf/white.png';
import blackUS from 'assets/images/ribbons/cabinetRibbon/upperShelf/black.png';
import naturalUS from 'assets/images/ribbons/cabinetRibbon/upperShelf/natural.png';
import {
  makeSelectCabinetDepth,
  makeSelectCabinetWidth,
} from 'containers/Configurator/selectors/furniture';
import {
  marksDepth,
  marksWidth,
} from 'containers/Ribbon/CabinetRibbon/constants';
import CornerSelector from 'containers/Ribbon/CabinetRibbon/CornerSelector';
import BigSelector from 'components/BigSelector';
import { displaySupportPanelAction } from 'containers/SupportPanel/actions';
import { StyledRibbonWrapper } from 'containers/Ribbon/StyledRibbonWrapper';
import { useMedia } from 'use-media';
import MobileMenuCabinet from 'containers/Ribbon/CabinetRibbon/MobileMenuCabinet';
import {
  makeSelectContext,
  makeSelectIsActiveCustomMode,
} from 'containers/Configurator/selectors/general';
import { useTranslation } from 'react-i18next';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import FurnitureSection from 'containers/Ribbon/FurnitureSection';
import { makeSelectLastHistoryCabinet } from 'containers/Configurator/selectors/history';
import {
  BLACK_COLOR,
  CabinetSupport,
  NATURAL_COLOR,
} from 'lib/modelBuilder/constants';
import Button from 'components/Button';
import addAShelf from 'assets/images/ribbons/cabinetRibbon/addShelf.svg';
import { displayUpperShelfPanelAction } from '../../UpperShelfPanel/actions';
import CabinetOptions from './CabinetOptions';
import { FurnitureEditorContext } from '../../../lib/constraintsChecker/FurnitureEditor';
import { isProduction } from '../../../utils/constants';

interface StateProps {
  width: number;
  depth: number;
  custom: boolean;
  cabinet: Cabinet | null;
  context: FurnitureEditorContext;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  width: makeSelectCabinetWidth(),
  depth: makeSelectCabinetDepth(),
  custom: makeSelectIsActiveCustomMode(),
  cabinet: makeSelectLastHistoryCabinet(),
  context: makeSelectContext(),
});

function CabinetRibbon() {
  useInjectReducer({ key: 'cabinetRibbon', reducer: reducer });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { width, depth, custom, cabinet, context } = useSelector(stateSelector);
  const isMobile = useMedia({ maxWidth: 1023 });
  const isXLargeScreen = useMedia({ minWidth: 1280 });

  const updateCabinetDepth = (value: number, preview: boolean): void => {
    dispatch(setDepthRequest(value, preview));
  };

  const updateCabinetWidth = (value: number, preview: boolean): void => {
    dispatch(setWidthRequest(value, preview));
  };

  const getCabinetSupportTitle = () => {
    if (!cabinet) return t('Feets');

    switch (cabinet.support) {
      case CabinetSupport.NoSupport:
        return t('No feets');
      case CabinetSupport.Feet:
        return t('Classic feets');
      case CabinetSupport.AdjustableFeet:
        return t('Adjustable feets');
      case CabinetSupport.WheelWhite:
      case CabinetSupport.WheelBlack:
        return t('Wheels');
      case CabinetSupport.ToHangScrew:
      case CabinetSupport.ToHangHook:
        return t('Suspension');
    }
  };

  const getCabinetSupportImg = () => {
    if (!cabinet) return t('Feets');

    switch (cabinet.support) {
      case CabinetSupport.NoSupport:
        return noFeets;
      case CabinetSupport.Feet:
        return feets10;
      case CabinetSupport.AdjustableFeet:
        return feetsAdjustable10;
      case CabinetSupport.WheelWhite:
        return wheelsWhite;
      case CabinetSupport.WheelBlack:
        return wheelsBlack;
      case CabinetSupport.ToHangScrew:
      case CabinetSupport.ToHangHook:
        return noFeets;
    }
  };

  const getUpperShelfImg = () => {
    if (!cabinet || !cabinet.upperShelf) return whiteUS;

    switch (cabinet.upperShelf.color.reference) {
      case BLACK_COLOR:
        return blackUS;
      case NATURAL_COLOR:
        return naturalUS;
      default:
        return whiteUS;
    }
  };

  function handleAddShelf() {
    dispatch(addShelf());
  }

  if (!isMobile) {
    return (
      <StyledRibbonWrapper>
        <RibbonSection border>
          <div className="lg:-mt-2 xl:-mt1">
            <Button
              title={t('Add a shelf')}
              onClick={handleAddShelf}
              img={addAShelf}
              small={false}
            />
          </div>
        </RibbonSection>
        <div className="h-20 border-l border-gray-400" />

        <RibbonSection
          title={t('Furniture dimension')}
          className="tuto-step-cabinet-dimensions"
          border
        >
          <div className="flex flex-col">
            <DimensionCabinetSelector
              value={width}
              icon={sizeWidth}
              handleInput={updateCabinetWidth}
              min={marksWidth[0].value}
              max={marksWidth[marksWidth.length - 1].value}
              marks={marksWidth}
              custom={custom}
            />
            <DimensionCabinetSelector
              value={depth}
              icon={sizeDepth}
              handleInput={updateCabinetDepth}
              min={marksDepth[0].value}
              max={marksDepth[marksDepth.length - 1].value}
              marks={marksDepth}
              custom={custom}
              className="lg:-mt-2 xl:-mt-0"
            />
          </div>
        </RibbonSection>
        <div className="h-20 border-l border-gray-400" />

        <RibbonSection
          title={t('Corner piece')}
          className="tuto-step-cabinet-corners"
          border
        >
          <CornerSelector />
        </RibbonSection>
        <div className="h-20 border-l border-gray-400" />

        <RibbonSection
          title={t('Support/Fixation')}
          className="w-32 tuto-step-cabinet-support"
          border
        >
          <div className="lg:mt-1">
            <BigSelector
              imgSrc={getCabinetSupportImg()}
              imgAlt={t('support')}
              title={getCabinetSupportTitle()}
              handleClick={() => {
                dispatch(displaySupportPanelAction(true));
              }}
            />
          </div>
        </RibbonSection>
        <div className="h-20 border-l border-gray-400" />

        {cabinet?.upperShelf ? (
          <RibbonSection
            title={t('Upper shelf')}
            border
            className="w-32 tuto-step-cabinet-plate"
          >
            <div className="lg:mt-1">
              <BigSelector
                imgSrc={getUpperShelfImg()}
                imgAlt={
                  cabinet.upperShelf?.base.color.reference === NATURAL_COLOR
                    ? `MDF ${cabinet?.upperShelf?.color.name}`
                    : cabinet?.upperShelf?.color.name
                }
                title={
                  cabinet.upperShelf?.base.color.reference === NATURAL_COLOR
                    ? `MDF ${cabinet?.upperShelf?.color.name}`
                    : cabinet?.upperShelf?.color.name
                }
                handleClick={() => {
                  dispatch(displayUpperShelfPanelAction(true));
                }}
              />
            </div>
          </RibbonSection>
        ) : (
          <RibbonSection
            title={isXLargeScreen ? t('Option') : undefined}
            border
          >
            <div className="lg:-mt-2 xl:mt-1">
              <Button
                title={t('Upper shelf')}
                onClick={() => {
                  dispatch(displayUpperShelfPanelAction(true));
                }}
                img={addUS}
                small={false}
              />
            </div>
          </RibbonSection>
        )}

        <div className="h-20 border-l border-gray-400" />

        <RibbonSection>
          <div className="lg:-mt-2 xl:-mt1">
            <FurnitureSection />
          </div>
        </RibbonSection>
        {(context === FurnitureEditorContext.sales || !isProduction) && (
          <CabinetOptions />
        )}
      </StyledRibbonWrapper>
    );
  } else {
    return (
      <MobileMenuCabinet
        width={width}
        depth={depth}
        updateCabinetWidth={updateCabinetWidth}
        updateCabinetDepth={updateCabinetDepth}
      />
    );
  }
}
export default CabinetRibbon;
